import React, { useEffect, useState } from "react";
import TopBar from "@/components/TopBar";
import { api, formatApiError } from "@/lib/api";
import { useParams } from "react-router-dom";
import { User, TrendingUp, TrendingDown, BarChart3, Users, Calendar, Target } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

function fmt(n, d = 2) {
  if (n == null || Number.isNaN(n)) return "—";
  return Number(n).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
}

export default function TraderProfile() {
  const { t } = useI18n();
  const { id } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/social/profile/${id}`)
      .then(({ data }) => setProfile(data))
      .catch((e) => console.error(formatApiError(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="min-h-screen bg-app text-white"><TopBar /><div className="text-dim text-sm py-12 text-center">{t("loading")}</div></div>;
  if (!profile) return <div className="min-h-screen bg-app text-white"><TopBar /><div className="text-dim text-sm py-12 text-center">{t("trader_not_found")}</div></div>;

  return (
    <div className="min-h-screen bg-app text-white">
      <TopBar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <div className="bg-panel border border-line rounded-sm p-5 sm:p-6 mb-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-full bg-buy/10 border border-buy/30 flex items-center justify-center">
              <User className="w-7 h-7 text-buy" />
            </div>
            <div>
              <h1 className="text-xl font-bold">{profile.display_name}</h1>
              <div className="text-xs text-dim">{profile.name_masked}</div>
              <div className="text-[10px] text-dim flex items-center gap-2 mt-1">
                <Calendar className="w-3 h-3" /> {t("trader_member_since")} {profile.member_since ? new Date(profile.member_since).toLocaleDateString("tr-TR") : "—"}
              </div>
            </div>
            <div className="ml-auto text-right">
              <div className="text-xs text-dim">{t("trader_followers")}</div>
              <div className="text-lg font-bold font-mono flex items-center gap-1"><Users className="w-4 h-4 text-yellow-500" /> {profile.followers}</div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatBox label={t("trader_total_trades")} value={profile.total_trades} color="text-blue-400" />
            <StatBox label={t("trader_win_rate")} value={`%${profile.win_rate}`} color={profile.win_rate >= 50 ? "text-buy" : "text-sell"} />
            <StatBox label={t("trader_total_pnl")} value={`${profile.total_pnl >= 0 ? "+" : ""}$${fmt(profile.total_pnl)}`} color={profile.total_pnl >= 0 ? "text-buy" : "text-sell"} />
            <StatBox label={t("trader_assets")} value={profile.symbols_traded} color="text-yellow-500" />
          </div>
        </div>

        {/* Monthly PnL */}
        <div className="bg-panel border border-line rounded-sm p-5 mb-6">
          <h2 className="text-sm font-bold tracking-widest uppercase text-purple-400 mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4" /> {t("trader_monthly_performance")}
          </h2>
          <div className="grid grid-cols-6 gap-2">
            {profile.monthly_pnl.map((m) => (
              <div key={m.month} className="bg-app rounded-sm p-2 text-center">
                <div className="text-[9px] text-dim font-mono">{m.month.slice(5)}/{m.month.slice(2, 4)}</div>
                <div className={`text-xs font-bold font-mono ${m.pnl >= 0 ? "text-buy" : "text-sell"}`}>
                  {m.pnl >= 0 ? "+" : ""}${fmt(m.pnl)}
                </div>
                <div className="text-[9px] text-dim">{m.trades} {t("trader_trades_abbr")}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Trades */}
        <div className="bg-panel border border-line rounded-sm p-5">
          <h2 className="text-sm font-bold tracking-widest uppercase text-blue-400 mb-4 flex items-center gap-2">
            <Target className="w-4 h-4" /> {t("trader_recent_trades")}
          </h2>
          {profile.recent_trades.length === 0 ? (
            <div className="text-dim text-xs py-4">{t("trader_no_trades")}</div>
          ) : (
            <div className="space-y-1.5">
              {profile.recent_trades.map((t, i) => (
                <div key={i} className="flex items-center gap-3 bg-app border border-subtle/30 rounded-sm px-3 py-2">
                  <span className={`text-xs font-bold uppercase ${t.direction === "call" || t.direction === "buy" ? "text-buy" : "text-sell"}`}>
                    {t.direction === "call" || t.direction === "buy" ? <TrendingUp className="w-3.5 h-3.5 inline" /> : <TrendingDown className="w-3.5 h-3.5 inline" />}
                  </span>
                  <span className="text-xs font-bold">{t.symbol}</span>
                  <span className="text-[10px] text-dim uppercase">{t.type}</span>
                  <span className="flex-1" />
                  <span className={`font-mono text-xs ${t.pnl >= 0 ? "text-buy" : "text-sell"}`}>
                    {t.pnl >= 0 ? "+" : ""}${fmt(t.pnl)}
                  </span>
                  <span className="text-[10px] text-dim font-mono">{t.closed_at ? new Date(t.closed_at).toLocaleDateString("tr-TR") : "—"}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatBox({ label, value, color }) {
  return (
    <div className="bg-app rounded-sm p-3 text-center">
      <div className="text-[10px] uppercase tracking-widest text-dim mb-1">{label}</div>
      <div className={`font-mono text-lg font-bold ${color}`}>{value}</div>
    </div>
  );
}
